import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { EditableField } from '~/eds/src/components/EditableField/EditableField';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { TextArea } from '~/eds/src/components/TextArea/TextArea';
import { useIntl } from 'react-intl';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';

import AutomationHubSearchingFilters from '../../AutomationHubSearchFilters/AutomationHubSearchingFilters';

const FilterSummary = () => {
  const intl = useIntl();
  const model = useSelector(slices.automation.selectors.selectAutomationModel);
  const [description, setDescription] = useState(model.description || '');

  const [updateDescription] = api.endpoints.updateDescription.useMutation();

  const trainingFilter = model.current?.filter?.trainingFilter ?? [];
  const predictionFilter = model.current?.filter?.predictionFilter ?? [];

  const rules = model?.current?.userInput?.rules ?? {
    positive: [],
    negative: [],
  };
  const numOfPositiveRules = rules.positive.length;
  const numOfNegativeRules = rules.negative.length;

  const phrases = model?.current?.userInput?.phrases ?? {
    positive: [],
    negative: [],
  };
  const numOfPositivePhrases = phrases.positive.length;
  const numOfNegativePhrases = phrases.negative.length;

  const TrainingFilterSummary = useMemo(
    () => (
      <Layout direction="column" spacing={4}>
        <Text variant="body-medium">
          {intl.formatMessage({
            id: 'FilterSummary.trainingFiltersLabel',
            defaultMessage: 'Training Filters',
            description: 'Label for training filters summary',
          })}
        </Text>
        <AutomationHubSearchingFilters preview={trainingFilter} />
      </Layout>
    ),
    [trainingFilter],
  );

  const PredictFilterSummary = useMemo(
    () => (
      <Layout direction="column" spacing={4}>
        <Text variant="body-medium">
          {intl.formatMessage({
            id: 'FilterSummary.predictiveFiltersLabel',
            defaultMessage: 'Predictive Filters',
            description: 'Label for predictive filters summary',
          })}
        </Text>
        <AutomationHubSearchingFilters preview={predictionFilter} />
      </Layout>
    ),
    [predictionFilter],
  );

  const handleUpdateDescription = (description) => {
    setDescription(description.trim());
    updateDescription({ clauseId: model.id, description: description.trim() });
  };

  return (
    <Layout border="border" boxShadow="base" direction="column">
      <Layout p={6} direction="column" spacing={6}>
        <EditableField
          input={TextArea}
          label={intl.formatMessage({
            id: 'FilterSummary.descriptionLabel',
            defaultMessage: 'Description',
            description: 'Label for description field',
          })}
          placeholder={intl.formatMessage({
            id: 'FilterSummary.descriptionPlaceholder',
            defaultMessage: 'Please add the description',
            description: 'Placeholder for description field',
          })}
          value={description}
          onSave={handleUpdateDescription}
          getValueMessage={(value) => {
            if (value?.length > 500) {
              return {
                message: intl.formatMessage(
                  {
                    id: 'FilterSummary.descriptionLengthError',
                    defaultMessage:
                      '{count}/500 (Description cannot be longer than 500 characters)',
                    description: 'Error message for description length limit',
                  },
                  { count: value.length },
                ),
                status: 'error',
              };
            } else {
              return { message: '', status: 'info' };
            }
          }}
        />
        {TrainingFilterSummary}
        {PredictFilterSummary}
        <Layout direction="column" spacing={4}>
          <Text variant="body-medium">
            {intl.formatMessage({
              id: 'FilterSummary.rulesLabel',
              defaultMessage: 'Rules',
              description: 'Label for rules section',
            })}
          </Text>
          <Text>
            {numOfPositiveRules + numOfNegativePhrases <= 0 ? (
              intl.formatMessage({
                id: 'FilterSummary.noneLabel',
                defaultMessage: 'None',
                description: 'Label for none/no items',
              })
            ) : (
              <>
                {intl.formatMessage(
                  {
                    id: 'FilterSummary.positiveNegativeRules',
                    defaultMessage: '{positive} positive, {negative} negative',
                    description:
                      'Summary of positive and negative rules. Example: positive=3, negative=2',
                  },
                  {
                    positive: numOfPositiveRules,
                    negative: numOfNegativeRules,
                  },
                )}
              </>
            )}
          </Text>
        </Layout>
        <Layout direction="column" spacing={4}>
          <Text variant="body-medium">
            {intl.formatMessage({
              id: 'FilterSummary.phrasesLabel',
              defaultMessage: 'Phrases',
              description: 'Label for phrases section',
            })}
          </Text>
          <Text>
            {numOfPositivePhrases + numOfNegativePhrases <= 0 ? (
              intl.formatMessage({
                id: 'FilterSummary.noneLabel',
                defaultMessage: 'None',
                description: 'Label for none/no items',
              })
            ) : (
              <>
                {intl.formatMessage(
                  {
                    id: 'FilterSummary.positiveNegativePhrases',
                    defaultMessage: '{positive} positive, {negative} negative',
                    description:
                      'Summary of positive and negative phrases. Example: positive=3, negative=2',
                  },
                  {
                    positive: numOfPositivePhrases,
                    negative: numOfNegativePhrases,
                  },
                )}
              </>
            )}
          </Text>
        </Layout>
      </Layout>
    </Layout>
  );
};

export default FilterSummary;
